import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Vakt for navigasjon bort fra et skjema med ulagrede endringer.
 *
 * Planregisteret har ingen router, så vakten sitter rundt navigasjons-
 * callbackene: `guard(action)` kjører handlingen direkte når skjemaet er rent,
 * ellers holdes den tilbake til brukeren velger å forkaste (`confirm`) eller
 * bli (`cancel`). Lukking/reload av fanen varsles via `beforeunload`.
 */
export function useUnsavedChangesGuard() {
    const [isDirty, setDirty] = useState(false);
    const [pending, setPending] = useState<(() => void) | null>(null);
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    useEffect(() => {
        if (!isDirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            // Eldre nettlesere krever returnValue for å vise dialogen.
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty]);

    const guard = useCallback((action: () => void) => {
        if (isDirtyRef.current) {
            setPending(() => action);
        } else {
            action();
        }
    }, []);

    const confirm = useCallback(() => {
        const action = pending;
        setPending(null);
        setDirty(false);
        action?.();
    }, [pending]);

    const cancel = useCallback(() => setPending(null), []);

    return { isDirty, setDirty, guard, isConfirming: pending !== null, confirm, cancel };
}

export type UnsavedChangesGuard = ReturnType<typeof useUnsavedChangesGuard>;
